import { Tree, NodeRendererProps } from "react-arborist";
import { TreeNode } from "../lib/api";

// Stable empty-children reference so a childless root doesn't hand react-arborist
// a fresh `[]` identity every render (controlled `data` reconciles on identity).
const EMPTY: TreeNode[] = [];

/**
 * Render the deposit's directory tree using react-arborist.
 *
 * react-arborist expects a flat data array OR a recursive `children` array.
 * Our TreeNode already has children, so we pass it through.
 *
 * Selection is owned by the parent — we just call `onSelect(path)` when the
 * reviewer activates a leaf node. Lazy stubs (M4.3) call `onLazyClick` with
 * the node so the parent can drive the download UX.
 */
export default function FileTree(props: {
  node: TreeNode;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  onLazyClick?: (node: TreeNode) => void;
}) {
  return (
    <Tree<TreeNode>
      // Controlled `data` (not `initialData`): react-arborist reads
      // `initialData` only once at mount, so a post-download `setTree` never
      // reached the view and a finished lazy file stayed a stub until reload
      // (issue #126). `data` re-renders on change; open/selection state is
      // keyed by node id (stable paths), so expansion survives the refresh.
      data={props.node.children ?? EMPTY}
      openByDefault={false}
      width="100%"
      height={9999}                 // arborist needs a number; outer flex clamps
      indent={14}
      rowHeight={22}
      onActivate={(node) => {
        if (node.data.isDir) return;
        if (node.data.lazyFileId != null && node.data.lazyStatus !== "ready") {
          props.onLazyClick?.(node.data);
          return;
        }
        props.onSelect(node.data.id);
      }}
    >
      {(rowProps) => <FileTreeRow {...rowProps} selectedPath={props.selectedPath} />}
    </Tree>
  );
}

function FileTreeRow(
  props: NodeRendererProps<TreeNode> & { selectedPath: string | null },
) {
  const { node, style, dragHandle, selectedPath } = props;
  const isSelected = !node.data.isDir && selectedPath === node.data.id;
  const isLazy =
    node.data.lazyFileId != null && node.data.lazyStatus !== "ready";
  const lazyBadge =
    node.data.lazyStatus === "downloading"
      ? "↓ …"
      : node.data.lazyStatus === "failed"
        ? "↻"
        : "↓";
  return (
    <div
      ref={dragHandle}
      style={style}
      className={`flex cursor-pointer items-center gap-1 px-2 text-xs ${
        isSelected ? "bg-ink-200" : "hover:bg-ink-100"
      }`}
      onClick={() => {
        if (node.data.isDir) node.toggle();
        else node.activate();
      }}
    >
      <span className="w-3 text-ink-500">
        {node.data.isDir ? (node.isOpen ? "▾" : "▸") : ""}
      </span>
      <span
        className={
          node.data.isDir
            ? "font-medium text-ink-700"
            : isLazy
              ? "text-ink-500 italic"
              : "text-ink-900"
        }
      >
        {node.data.name}
      </span>
      {isLazy && node.data.size != null && (
        <span className="ml-auto rounded bg-amber-100 px-1 text-[10px] font-mono text-amber-900">
          {lazyBadge} {humanSize(node.data.size)}
        </span>
      )}
    </div>
  );
}

function humanSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}
